import { DownloadSimpleIcon, LockSimpleIcon } from '@phosphor-icons/react'
import React from 'react'

import LoadingDots from '../_shared/LoadingDots'

export interface ReceivedCardActionsProps {
  /** Whether the card is currently locked. Drives which CTA we render. */
  isLocked: boolean
  /**
   * When unlocked, the URL used as the Download link's `href` (opens in a new
   * tab). When omitted, the Download CTA falls back to a plain `<button>`
   * that just calls `onDownloadClicked` — useful for galleries where the
   * single-source URL doesn't exist.
   */
  sourceUrl?: string
  /** Optional alternate href that takes precedence over `sourceUrl`. */
  redeemUrl?: string
  onUnlockClicked?: () => void
  onDownloadClicked?: () => void
  isUnlocking?: boolean
}

const buttonClass =
  'mt-3 inline-flex h-10 w-full items-center justify-center gap-2 rounded-full bg-[#121110] px-4 text-sm font-medium leading-none text-white hover:bg-[#2a2928]'

/**
 * Renders the primary CTA below the body of a Received card.
 * - Locked + onUnlockClicked → "Unlock" button
 * - Unlocked + onDownloadClicked + (redeemUrl || sourceUrl) → "Download" link
 * - Unlocked + onDownloadClicked + no URL → "Download" button (e.g. gallery)
 * Otherwise renders nothing.
 */
const ReceivedCardActions: React.FC<ReceivedCardActionsProps> = ({
  isLocked,
  sourceUrl,
  redeemUrl,
  onUnlockClicked,
  onDownloadClicked,
  isUnlocking = false,
}) => {
  if (isLocked && onUnlockClicked != null) {
    return (
      <button
        type="button"
        onClick={onUnlockClicked}
        disabled={isUnlocking}
        aria-label={isUnlocking ? 'Unlocking' : 'Unlock'}
        aria-busy={isUnlocking}
        title={isUnlocking ? 'Unlocking' : 'Unlock'}
        className={buttonClass}
      >
        {isUnlocking ? (
          <LoadingDots />
        ) : (
          <React.Fragment>
            <LockSimpleIcon className="size-4" weight="fill" />
            Unlock
          </React.Fragment>
        )}
      </button>
    )
  }

  if (!isLocked && onDownloadClicked != null) {
    const href = redeemUrl ?? sourceUrl
    if (href != null) {
      return (
        <a
          href={href}
          aria-label="Download"
          target="_blank"
          rel="noopener noreferrer"
          onClick={onDownloadClicked}
          className={`${buttonClass} !text-white`}
        >
          <DownloadSimpleIcon className="size-4" weight="bold" />
          Download
        </a>
      )
    }

    return (
      <button
        type="button"
        onClick={onDownloadClicked}
        aria-label="Download"
        className={buttonClass}
      >
        <DownloadSimpleIcon className="size-4" weight="bold" />
        Download
      </button>
    )
  }

  return null
}

export default ReceivedCardActions
